import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { H6, IconButton, Overline, Row, Subheading } from '@fs/zion-ui'
import { ContentEdit } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { getEventSummaryFromField } from '../utils/events.utils'

/**
 * PrimaryEventView
 *
 * Displays an existing primary event in read-only mode.
 * Shows event type as primary text, date/place as secondary text,
 * and an edit button to enter edit mode.
 *
 * This component is purely presentational with no internal state.
 *
 * @param {Object} props
 * @param {string} props.eventTypeLabel - Display label for the event type
 * @param {string} [props.eventData] - Secondary text (date | place)
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @returns {JSX.Element}
 */
const PrimaryEventView = ({ event, onEdit, disableEdit }) => {
  const [t] = useTranslation()
  const eventData = useMemo(() => getEventSummaryFromField(event), [event])

  return (
    <Row alignY="middle">
      <H6 supplementary growX>
        <Overline>{t('eventsForm.primaryEventHeader', 'Primary Event')}</Overline>
        {event?.eventType?.primaryText}
        {eventData && <Subheading>{eventData}</Subheading>}
      </H6>
      <IconButton
        Icon={ContentEdit}
        color="gray"
        onClick={onEdit}
        aria-label={t('navigation.edit-button', 'Edit')}
        disabled={disableEdit}
      />
    </Row>
  )
}

PrimaryEventView.propTypes = {
  event: PropTypes.shape({
    eventType: PropTypes.shape({
      primaryText: PropTypes.string,
    }),
    // eslint-disable-next-line react/forbid-prop-types
    dates: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    places: PropTypes.array,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
}

export default PrimaryEventView
